import { PENDING_EMAIL_CHANGE_STORAGE_KEY } from "@/pages/UpdateEmail/constants";

export type PendingEmailChange = {
  pendingEmail: string;
  expiresAt: number;
};

/**
 * Session-scoped so the snapshot dies with the tab. It never holds the verification code, only
 * where the flow was: the code lives in the user's inbox and on the server.
 */
const getStorage = (): Storage | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch {
    // Blocked storage (private mode, disabled cookies) only costs the user a new code.
    return null;
  }
};

const isPendingEmailChange = (candidate: unknown): candidate is PendingEmailChange => {
  const { pendingEmail, expiresAt } = (candidate ?? {}) as Partial<PendingEmailChange>;
  return typeof pendingEmail === "string" && pendingEmail.length > 0 && typeof expiresAt === "number";
};

export const readPendingEmailChange = (): PendingEmailChange | null => {
  const storage = getStorage();
  if (!storage) {
    return null;
  }

  try {
    const raw = storage.getItem(PENDING_EMAIL_CHANGE_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed: unknown = JSON.parse(raw);
    return isPendingEmailChange(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const writePendingEmailChange = (pending: PendingEmailChange): void => {
  const storage = getStorage();
  if (!storage) {
    return;
  }

  try {
    storage.setItem(PENDING_EMAIL_CHANGE_STORAGE_KEY, JSON.stringify(pending));
  } catch {
    // A lost snapshot degrades to restarting the flow, which is recoverable.
  }
};

export const clearPendingEmailChange = (): void => {
  const storage = getStorage();
  if (!storage) {
    return;
  }

  try {
    storage.removeItem(PENDING_EMAIL_CHANGE_STORAGE_KEY);
  } catch {
    // Nothing actionable: an orphan snapshot expires on its own.
  }
};
